import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';

import { WelfareProvider } from '@/dataAccess/rest/measurement/measurement.types.ts';

import { storeNames } from './config/config.type.ts';

/**
 * Persists only the user's welfareType selection per widget.
 * id/name come from layout storage or configInput — this store does not shadow them.
 */
interface WelfareIndicatorConfigStore {
  welfareTypeByWidgetId: Record<string, WelfareProvider>;
  setWelfareType: (widgetId: string, welfareType: WelfareProvider) => void;
}

const useWelfareIndicatorConfigZustandStore = create(
  persist<WelfareIndicatorConfigStore>(
    set => ({
      welfareTypeByWidgetId: {},
      setWelfareType: (widgetId, welfareType) =>
        set(state => {
          const previousWelfareType = state.welfareTypeByWidgetId[widgetId];

          // Skip no-op updates so subscribers are not notified with a new reference.
          if (previousWelfareType === welfareType) {
            return state;
          }

          return {
            welfareTypeByWidgetId: {
              ...state.welfareTypeByWidgetId,
              [widgetId]: welfareType,
            },
          };
        }),
    }),
    {
      name: storeNames.welfareIndicator,
      storage: createJSONStorage(() => localStorage),
    }
  )
);

export const useWelfareIndicatorConfigStorage = (widgetId: string) => {
  const { storedWelfareType, setWelfareType } = useWelfareIndicatorConfigZustandStore(
    useShallow(s => ({
      storedWelfareType: s.welfareTypeByWidgetId[widgetId],
      setWelfareType: s.setWelfareType,
    }))
  );

  return {
    welfareType: storedWelfareType,
    setWelfareType: (welfareType: WelfareProvider) => setWelfareType(widgetId, welfareType),
  };
};
